import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups/QuestionAnswerLanguageSelectPopup';
import { CreateMemoryGameProcessedContentTemplate } from '../../components/Templates';
import { getLanguageDetail } from '../../constants/languageDetails';
import { AppToastInfoOption } from '../../constants/toast';
import { usePopup } from '../../context';
import { TtsLocale } from '../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../models/DraftStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';

interface State {
  createType: QuickCreateGameMode | undefined;
  gameContents: string[];
  useTts: boolean;
  selectedLanguage: TtsLocale | undefined;
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  isFlashing: boolean;
}

const CreateMemoryGameProcessedContentScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showPopup, hidePopup } = usePopup();
  const [state, setState] = useState<State>({
    createType: undefined,
    gameContents: [''],
    useTts: true,
    selectedLanguage: undefined,
    isNextDisabled: false,
    isExceedLimit: false,
    isFlashing: false,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );

  const { mode, locale, drafts } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _locale = store.getLocale();
    const _gameDrafts = store.getGameDrafts();
    return { mode: _mode, locale: _locale, drafts: _gameDrafts };
  });

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    if (locale) {
      setState((s) => ({ ...s, selectedLanguage: locale }));
    }
  }, [locale]);

  useEffect(() => {
    if (drafts && drafts.length > 0) {
      const gameContents = drafts
        .map((draft) => draft.game.answer.text?.text)
        .filter((str) => str);
      if (gameContents.length > 0) {
        setState((s) => ({ ...s, gameContents: gameContents }));
      } else {
        navigate('/create/memory-game/input-content', { replace: true });
      }
    }
  }, [drafts, navigate]);

  useEffect(() => {
    const showToast = useDraftStore.getState().isNeedToShowDividedPopup();
    if (showToast) {
      toast(
        t(`Content is too long and divided into multiple fills.`),
        AppToastInfoOption,
      );
    }
  }, [t]);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const onClickNext = useCallback(() => {
    useDraftStore.getState().setShowDividedPopup(true);
    navigate('/create/memory-game/generated-games');
  }, [navigate]);

  const handleLanguagePopup = useCallback(
    (selectedLocale: TtsLocale) => {
      const languageList = state.createType
        ? getQuickCreateGameModeProps(
            state.createType,
            userAccount?.settings.webBoosterType,
          ).languageList
        : undefined;

      showPopup(
        <QuestionAnswerLanguageSelectPopup
          languageList={languageList}
          selectedLanguage={selectedLocale}
          recentLanguage={undefined}
          onClickOK={() => {
            hidePopup();
          }}
          onCancel={hidePopup}
          onSelectLanguage={(language: TtsLocale) => {
            setState((s) => ({ ...s, selectedLanguage: language }));
            useDraftStore.getState().setLocale(language);
            handleLanguagePopup(language); // Re-show popup with updated locale
          }}
        />,
      );
    },
    [
      hidePopup,
      showPopup,
      state.createType,
      userAccount?.settings.webBoosterType,
    ],
  );

  const onClickLanguage = useCallback(() => {
    if (state.selectedLanguage) {
      handleLanguagePopup(state.selectedLanguage);
    }
  }, [handleLanguagePopup, state.selectedLanguage]);

  const exceedContentWarning = useCallback(() => {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
      setState((s) => ({
        ...s,
        isFlashing: true,
      }));
      setTimeout(() => {
        setState((s) => ({
          ...s,
          isFlashing: false,
        }));
      }, 3000);
    }, 100);
  }, []);

  useEffect(() => {
    if (state.gameContents.length === 0) {
      setState((s) => ({ ...s, isNextDisabled: true }));
    } else if (state.gameContents.length > 20) {
      setState((s) => ({ ...s, isNextDisabled: true, isExceedLimit: true }));
      exceedContentWarning();
    } else {
      setState((s) => ({ ...s, isNextDisabled: false, isExceedLimit: false }));
    }
  }, [exceedContentWarning, state.gameContents.length]);

  const expectedPayment = useMemo(() => {
    const gameNum = state.gameContents.filter(
      (gameContent) => gameContent.trim() !== '',
    ).length;
    return (
      gameNum * (state.createType === QuickCreateGameMode.QuickMemory ? 3 : 2)
    );
  }, [state.gameContents, state.createType]);

  return (
    <CreateMemoryGameProcessedContentTemplate
      type={state.createType}
      webBoosterType={userAccount?.settings.webBoosterType}
      language={getLanguageDetail(state.selectedLanguage)?.displayName ?? ''}
      gameContents={state.gameContents}
      isNextDisabled={state.isNextDisabled}
      isExceedLimit={state.isExceedLimit}
      isFlashing={state.isFlashing}
      onClickLanguage={onClickLanguage}
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      expectedPayment={expectedPayment}
    />
  );
};
export default CreateMemoryGameProcessedContentScreen;
